import { Injectable } from '@angular/core';
import { IEntityTypeInfo, ISalesforceDisplayTemplates, SalesforceNamespace } from 'datasource-service-lib';
import { EntityControllerService } from 'entity-picker-lib';
import { IDataSourceApiService, IEntity, IEntityProfile } from 'interfaces-lib';
import { Observable, ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { renderDisplay } from 'salesforce-display-lib';
import { IEntityProfileService } from '../../app-entity-preview/ientityprofileservice';
import { ServiceProviderService } from '../service.provider.service';

@Injectable({ providedIn: 'root' })
export class SalesforceEntityProfileService implements IEntityProfileService {
  private apiService: IDataSourceApiService;
  private entityFieldsByType: Record<string, string[]> = {};
  private displayTemplatesByType: Record<string, ISalesforceDisplayTemplates | undefined> = {};

  private entityProfileSelectedSubject = new ReplaySubject<IEntityProfile>();
  entityProfileSelected$ = this.entityProfileSelectedSubject.asObservable();

  constructor(private entityControllerService: EntityControllerService, sps: ServiceProviderService) {
    this.apiService = sps.getDataSourceApiService(SalesforceNamespace.name);
    this.loadEntityFieldMap();
    this.entityControllerService.entitySelected$.subscribe((entity) => {
      if (!entity) {
        return;
      }
      this.getProfile(entity).subscribe((profile) => {
        this.entityProfileSelectedSubject.next(profile);
      });
    });
    console.debug('SalesforceEntityProfileService created');
  }

  getProfile(entity: IEntity): Observable<IEntityProfile> {
    return this.apiService.get(entity, { fields: this.getFields(entity) }).pipe(map((fatEntity) => this.transform(fatEntity)));
  }

  private getFields(entity: IEntity): string {
    const type = entity?.type;
    if (!type) {
      return 'Id,Name';
    }
    const fields = this.entityFieldsByType[type];
    if (!fields?.length) {
      return 'Id,Name';
    }
    return Array.from(new Set([...fields, 'Id', 'Name'])).join(',');
  }

  private loadEntityFieldMap(): void {
    const api = this.apiService as any;
    if (!api.getAvailableEntityTypes) {
      return;
    }
    api.getAvailableEntityTypes().subscribe({
      next: (types: IEntityTypeInfo[]) => {
        this.entityFieldsByType = types.reduce((acc, type) => {
          const fields = type.fields?.length ? type.fields : ['Id'];
          acc[type.objectName] = Array.from(new Set([...fields, 'Id', 'Name']));
          return acc;
        }, {} as Record<string, string[]>);
        this.displayTemplatesByType = types.reduce((acc, type) => {
          acc[type.objectName] = type.display;
          return acc;
        }, {} as Record<string, ISalesforceDisplayTemplates | undefined>);
      },
      error: () => {
        this.entityFieldsByType = {};
        this.displayTemplatesByType = {};
      },
    });
  }

  private transform(entityData: any): IEntityProfile | undefined {
    const template = this.displayTemplatesByType[entityData.type]?.primary;
    const name = renderDisplay(entityData, template);
    return {
      id: entityData.id || entityData.Id,
      type: entityData.type,
      name,
      contactPhones: [],
      contactEmails: [],
    } as IEntityProfile;
  }
}
